import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { DndProvider as DndProviderBase } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { SmallLoader } from 'shared/components/loading';
import { CustomNavigationItemType, GqlCustomNavigation, GqlCustomNavigationItem } from 'types/graphql';
import { ActiveSwitch } from 'src/components/active-switch';
import { MenuCustomizationLayout } from '../components/menu-customization-layout';
import { MobileSettingsMovedBanner } from '../components/mobile-settings-moved-banner';
import { Input, Label, SubText } from '../customize.styles';

import { useCustomNavigationLocations } from './hooks/use-custom-navigation-locations';
import { useCustomNavigationForm } from './hooks/use-custom-navigation-form';
import { NavigationItemSelector } from './components/navigation-item-selector';
import { DraggableNavigationItem } from './components/draggable-navigation-item';
import { NavigationBreadcrumbs } from './components/navigation-breadcrumbs';
import { CustomNavigationPreview } from './components/custom-navigation-preview';
import { ModalController } from './modals/modal-controller';
import { MAX_SECTION_DEPTH } from './custom-navigation.constants';

const DndProvider = DndProviderBase as any;

type CustomNavigationFormProps = {
  initialNavigation?: GqlCustomNavigation | null;
  /** Hides the "Custom Navigation" headerText/subHeaderText — used when this form is embedded
   *  somewhere that already shows an equivalent heading (see enterprise-admin/mobile-app). */
  hideHeader?: boolean;
};

export function CustomNavigationForm({
  initialNavigation,
  hideHeader = false,
}: CustomNavigationFormProps): JSX.Element {
  const flags = useFlags();
  const showNavigationFields = hideHeader || !(flags['ecomm.app.advanced-customization'] ?? false);
  const {
    currentPath,
    currentItems,
    isDirty,
    isEnabled,
    isLoading,
    isSaving: _isSaving,
    navigationItems,
    navigationLabel,
    handleAddItem,
    handleRemoveItem,
    handleMoveItem,
    handleEditItem,
    handleEnabledChange,
    handleLabelChange,
    handleSave,
    handleReset,
    handleNavigateToSection,
    handleNavigateToPath,
  } = useCustomNavigationForm({ initialNavigation });

  const [modalType, setModalType] = useState<CustomNavigationItemType | null>(null);
  const [editingItem, setEditingItem] = useState<GqlCustomNavigationItem | null>(null);

  const { hasAccessToAllLocations, chainId, totalLocationCount } = useCustomNavigationLocations();

  const handleOpenAddModal = useCallback((navigationType: CustomNavigationItemType) => {
    setModalType(navigationType);
    setEditingItem(null);
  }, []);

  const handleOpenEditModal = useCallback(
    (itemId: string) => {
      const findItem = (items: GqlCustomNavigationItem[]): GqlCustomNavigationItem | null =>
        items.reduce<GqlCustomNavigationItem | null>(
          (found, item) => found ?? (item.id === itemId ? item : findItem(item.items)),
          null
        );

      const item = findItem(navigationItems);
      if (item) {
        setModalType(item.navigationType);
        setEditingItem(item);
      }
    },
    [navigationItems]
  );

  const handleCloseModal = useCallback(() => {
    setModalType(null);
    setEditingItem(null);
  }, []);

  const handleSaveItem = useCallback(
    (item: GqlCustomNavigationItem) => {
      if (editingItem) {
        handleEditItem(item.id, item);
      } else {
        handleAddItem(item);
      }
    },
    [editingItem, handleEditItem, handleAddItem]
  );

  const handlePublish = useCallback(async () => {
    await handleSave();
  }, [handleSave]);

  const handlePublishToLocations = useCallback(
    async (dispensaryIds: string[]) => {
      await handleSave(dispensaryIds);
    },
    [handleSave]
  );

  const excludedTypes = useMemo(() => {
    if (currentPath.length >= MAX_SECTION_DEPTH) {
      return [CustomNavigationItemType.section];
    }
    return [];
  }, [currentPath.length]);

  if (isLoading) {
    return <SmallLoader />;
  }

  return (
    <MenuCustomizationLayout
      chainId={chainId}
      hasAccessToAllLocations={hasAccessToAllLocations}
      handleApplyToLocations={(dispensaryIds) => void handlePublishToLocations(dispensaryIds)}
      handlePublish={() => void handlePublish()}
      handleReset={handleReset}
      headerText='Custom Navigation'
      hideHeader={hideHeader}
      isDirty={isDirty}
      previewComponent={
        <CustomNavigationPreview
          isNested={currentPath.length > 0}
          items={currentItems}
          onNavigateUp={() => handleNavigateToPath(currentPath.slice(0, -1))}
          onSectionClick={handleNavigateToSection}
          title={navigationLabel}
        />
      }
      subHeaderText='Add a custom navigation menu to your mobile application. Drag items to reorder. Click into sections to manage nested items.'
      totalLocationCount={totalLocationCount}
    >
      {/* Custom Navigation is entirely mobile-app-specific -- unlike Theme/Product Cards, there's
          no shared desktop use for any of it. When ecomm.app.advanced-customization is on, the
          classic /dispensaries/:id/customize/custom-navigation page shows just the "They moved!"
          banner pointing at the editor under the enterprise Mobile App tab. When the flag is
          off, the classic page still renders the editor so the settings remain reachable. */}
      {!hideHeader && <MobileSettingsMovedBanner tabSegment='navigation' />}

      {showNavigationFields && (
        <DndProvider backend={HTML5Backend}>
          <Content>
            <Input>
              <ActiveSwitch
                name='navigationEnabled'
                active={isEnabled}
                onClick={handleEnabledChange as any}
                hasLabel
                usePropForValue
              />
            </Input>

            <Label htmlFor='navigationLabel'>Title</Label>
            <SubText>Provide a display title for your custom navigation.</SubText>
            <Input>
              <NavigationInput
                id='navigationLabel'
                onChange={(e) => handleLabelChange(e.target.value)}
                placeholder='Menu'
                type='text'
                value={navigationLabel}
              />
            </Input>

            <NavigationItemSelector excludedTypes={excludedTypes} onAddItem={handleOpenAddModal} />

            <SubText>Drag items to reorder. Click into sections to manage nested items.</SubText>
            <NavigationBreadcrumbs
              currentPath={currentPath}
              navigationItems={navigationItems}
              onNavigate={handleNavigateToPath}
              rootLabel={navigationLabel || 'Navigation'}
            />

            {currentItems.length === 0 ? (
              <EmptyState>
                <EmptyStateText>No navigation items yet.</EmptyStateText>
                <EmptyStateSubtext>Add items from the options above to build your custom navigation.</EmptyStateSubtext>
              </EmptyState>
            ) : (
              <NavigationItemsContainer>
                {currentItems.map((item, index) => (
                  <DraggableNavigationItem
                    key={item.id}
                    index={index}
                    item={item}
                    onEdit={handleOpenEditModal}
                    onMove={handleMoveItem}
                    onRemove={handleRemoveItem}
                    onSectionClick={handleNavigateToSection}
                  />
                ))}
              </NavigationItemsContainer>
            )}
          </Content>
        </DndProvider>
      )}

      {modalType && (
        <ModalController
          editingItem={editingItem}
          itemType={modalType}
          onClose={handleCloseModal}
          onSave={handleSaveItem}
        />
      )}
    </MenuCustomizationLayout>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const NavigationItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const EmptyState = styled.div`
  align-items: center;
  background: #f9fafb;
  border: 2px dashed #d3d8de;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px 24px;
`;

const EmptyStateText = styled.p`
  color: #4f5d68;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const EmptyStateSubtext = styled.p`
  color: #677882;
  font-size: 14px;
`;

const NavigationInput = styled.input`
  border: 1px solid #d3d8de;
  border-radius: 4px;
  max-width: 448px;
  padding: 8px;
  width: 100%;
`;
